import { FootballTeamPlayer } from "../../../commons/models/football/football-team-player";
import { AbstractBaseService } from "../../../ensue-react-system/utilities/base-service";
import { PaginatedEnsueCollection } from "../../../ensue-react-system/utilities/paginated-ensue-collection";
import { ManageFootballTeam } from "../../../commons/models/football/admin/manage-football-team";
import { EnsueCollection } from "../../../ensue-react-system/utilities/ensue-collection";
import { FootballAssociatedCompetition } from "../../../commons/models/football/football-associated-competition";
import { FootballBasicStatistics } from "../../../commons/models/football/football-basic-statistics";
import { FootballTeamManager } from "../../../commons/models/football/football-team-manager";
import { FootballTeamCoach } from "../../../commons/models/football/football-team-coach";
import { ManageFootballSubTeam } from "../../../commons/models/football/admin/manage-football-sub-team";
import { ManageFootballPlayer } from "../../../commons/models/football/admin/manage-football-player";
import { ManageFootballCompetition } from "../../../commons/models/football/admin/manage-football-competition";
import { ManageFootballHostedCompetition } from "../../../commons/models/football/admin/manage-football-hosted-competition";
import { FootballTournamentManager } from "../../../commons/models/football/football-tournament-manager";
import { FootballMatchDetail } from "../../../commons/models/football/football-match-detail";
import { ManageFootballCompetitionInvitation } from "../../../commons/models/football/admin/manage-football-competition-invitation";
import { User } from "../../../commons/models/user";

export interface ITeamPlayerDTO {
  jerseyNumber: number;
  startAt: string;
  isCaptain: boolean;
  playingRoles: string;
}

export interface IHostedCompetitionDTO {
  title: string;
  abbreviation: string;
  description: string | null;
  logo: string | null;
  type: string;
  teamGender: number | null;
  ageGroup: string | null;
  teamForm: string | null;
  skillLevel: string | null;
}

export interface ITeamMultiplePlayerDTO extends ITeamPlayerDTO {
  id: number;
}

export interface ITeamDTO {
  name: string;
  shortName: string;
  abbreviation: string;
  description: string | null;
  founded: string | null;
  logo: string | null;
  homeColor: string | null;
  awayColor: string | null;
  thirdColor: string | null;
  goalkeeperHomeColor: string | null;
  goalkeeperAwayColor: string | null;
  playersCount: number | null;
  status: number | null;
}

export interface ISubTeamDTO {
  name: string;
  shortName: string;
  abbreviation: string;
  description: string | null;
  founded: string | null;
  logo: string | null;
  homeColor: string | null;
  awayColor: string | null;
  thirdColor: string | null;
  goalkeeperHomeColor: string | null;
  goalkeeperAwayColor: string | null;
  status: number | null;
}

export interface ITeamChangeStatus {
  status: number;
}

export interface ITeamInviteDTO {
  selectedTeamId: number;
}

export class ManageFootballTeamService extends AbstractBaseService<ManageFootballTeam> {
  protected __model: ManageFootballTeam = new ManageFootballTeam();

  public getResourceName(): string {
    return "manage/football/teams";
  }

  public addTeam(teamData: ITeamDTO) {
    return this.__client.post(this.__url, teamData);
  }

  public editTeam(teamId: number, teamData: ITeamDTO) {
    return this.__client.patch(this.__url + "/" + teamId, teamData);
  }

  public setTeamInactive(teamId: number) {
    return this.__client.patch(this.__url + "/" + teamId + "/inactive", null);
  }

  public changeStatus(teamId: number, body: ITeamChangeStatus) {
    return this.__client.patch(this.__url + "/" + teamId + "/status", body);
  }

  public setTeamTerminate(teamId: number, date: string) {
    const terminateBody = {
      endAt: date,
    };
    return this.__client.patch(this.__url + "/" + teamId + "/terminate", terminateBody);
  }

  public getTeamPlayers(
    teamId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<PaginatedEnsueCollection<FootballTeamPlayer>> {
    const teamPlayerModel = new FootballTeamPlayer();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + teamId + "/players", options)
      .then((obj) => teamPlayerModel.createFromCollection(obj) as PaginatedEnsueCollection<FootballTeamPlayer>);
  }

  public addTeamMultiplePlayers(teamId: number, teamMultiplePlayersInfo: ITeamMultiplePlayerDTO[]) {
    return this.__client.post(this.__url + "/" + teamId + "/players", { players: teamMultiplePlayersInfo });
  }

  public editTeamPlayer(teamId: number, playerId: number, playerInfo: ITeamPlayerDTO) {
    return this.__client.patch(this.__url + "/" + teamId + "/players/" + playerId, playerInfo);
  }

  public removeTeamPlayer(teamId: number, playerId: number, date: string) {
    const playerRemoveBody = {
      endAt: date,
    };
    return this.__client.patch(this.__url + "/" + teamId + "/players/" + playerId + "/unassign", playerRemoveBody);
  }

  public getTeamCompetitions(
    teamId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<EnsueCollection<FootballAssociatedCompetition>> {
    const competitionModel = new FootballAssociatedCompetition();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + teamId + "/competitions", options)
      .then((obj) => competitionModel.createFromCollection(obj) as EnsueCollection<FootballAssociatedCompetition>);
  }

  public getStatistics(
    teamId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<FootballBasicStatistics> {
    const options = this.createHttpParamObject(urlParams);
    const statistics = new FootballBasicStatistics();
    return this.__client.get(this.__url + "/" + teamId + "/statistics", options).then((o) => {
      return statistics.create(o) as FootballBasicStatistics;
    });
  }

  public getTeamManagers(
    teamId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<PaginatedEnsueCollection<FootballTeamManager>> {
    const teamPlayerModel = new FootballTeamManager();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + teamId + "/managers", options)
      .then((obj) => teamPlayerModel.createFromCollection(obj) as PaginatedEnsueCollection<FootballTeamManager>);
  }

  public addTeamManager(teamId: number, managerId: number) {
    return this.__client.post(this.__url + "/" + teamId + "/users/" + managerId + "/roles/managers", null);
  }

  public removeTeamManager(teamId: number, managerId: number) {
    return this.__client.delete(this.__url + "/" + teamId + "/users/" + managerId + "/roles/managers");
  }

  public getTeamCoaches(
    teamId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<PaginatedEnsueCollection<FootballTeamCoach>> {
    const teamPlayerModel = new FootballTeamCoach();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + teamId + "/coaches", options)
      .then((obj) => teamPlayerModel.createFromCollection(obj) as PaginatedEnsueCollection<FootballTeamCoach>);
  }

  public addTeamCoach(teamId: number, userId: number) {
    return this.__client.post(this.__url + "/" + teamId + "/users/" + userId + "/roles/coaches", null);
  }

  public removeTeamCoach(teamId: number, userId: number) {
    return this.__client.delete(this.__url + "/" + teamId + "/users/" + userId + "/roles/coaches");
  }

  //sub-team

  public getSubTeams(
    teamId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<PaginatedEnsueCollection<ManageFootballSubTeam>> {
    const teamPlayerModel = new ManageFootballSubTeam();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + teamId + "/sub-teams", options)
      .then((obj) => teamPlayerModel.createFromCollection(obj) as PaginatedEnsueCollection<ManageFootballSubTeam>);
  }

  public getSubTeam(
    teamId: number,
    subTeamId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<ManageFootballSubTeam> {
    const teamPlayerModel = new ManageFootballSubTeam();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + teamId + "/sub-teams/" + subTeamId, options)
      .then((obj) => teamPlayerModel.create(obj) as ManageFootballSubTeam);
  }

  public addSubTeam(teamId: number, subTeamInfo: ISubTeamDTO) {
    return this.__client.post(this.__url + "/" + teamId + "/sub-teams/", subTeamInfo);
  }

  public editSubTeam(teamId: number, subTeamId: number, subTeamInfo: ISubTeamDTO) {
    return this.__client.patch(this.__url + "/" + teamId + "/sub-teams/" + subTeamId, subTeamInfo);
  }

  public removeSubTeam(teamId: number, subTeamId: number) {
    return this.__client.delete(this.__url + "/" + teamId + "/sub-teams/" + subTeamId);
  }

  public changeSubTeamStatus(teamId: number, subTeamId: number, body: ITeamChangeStatus) {
    return this.__client.patch(this.__url + "/" + teamId + "/sub-teams/" + subTeamId + "/status", body);
  }

  public getPlayersAvailableForTeam(
    teamId: number,
    urlParams?: {
      [key: string]: string | number | boolean;
    }
  ): Promise<PaginatedEnsueCollection<ManageFootballPlayer>> {
    const playerNotInTeamListModel = new ManageFootballPlayer();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + teamId + "/available-players", options)
      .then(
        (obj) => playerNotInTeamListModel.createFromCollection(obj) as PaginatedEnsueCollection<ManageFootballPlayer>
      );
  }

  public getUsersAvailableForTeam(
    teamId: number,
    urlParams?: {
      [key: string]: string | number | boolean;
    }
  ): Promise<PaginatedEnsueCollection<User>> {
    const playerNotInTeamListModel = new User();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + teamId + "/available-users", options)
      .then((obj) => playerNotInTeamListModel.createFromCollection(obj) as PaginatedEnsueCollection<User>);
  }

  public getPlayersAvailableForSubTeam(
    teamId: number,
    subTeamId: number,
    urlParams?: {
      [key: string]: string | number | boolean;
    }
  ): Promise<PaginatedEnsueCollection<ManageFootballPlayer>> {
    const playerNotInTeamListModel = new ManageFootballPlayer();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + teamId + "/sub-teams/" + subTeamId + "/available-players", options)
      .then(
        (obj) => playerNotInTeamListModel.createFromCollection(obj) as PaginatedEnsueCollection<ManageFootballPlayer>
      );
  }

  public getSubTeamPlayers(
    teamId: number,
    subTeamId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<PaginatedEnsueCollection<FootballTeamPlayer>> {
    const teamPlayerModel = new FootballTeamPlayer();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + teamId + "/sub-teams/" + subTeamId + "/players", options)
      .then((obj) => teamPlayerModel.createFromCollection(obj) as PaginatedEnsueCollection<FootballTeamPlayer>);
  }

  public addSubTeamMultiplePlayers(
    teamId: number,
    subTeamId: number,
    teamMultiplePlayersInfo: ITeamMultiplePlayerDTO[]
  ) {
    return this.__client.post(this.__url + "/" + teamId + "/sub-teams/" + subTeamId + "/players", {
      players: teamMultiplePlayersInfo,
    });
  }

  public editSubTeamPlayer(teamId: number, subTeamId: number, playerId: number, playerInfo: ITeamPlayerDTO) {
    return this.__client.patch(
      this.__url + "/" + teamId + "/sub-teams/" + subTeamId + "/players/" + playerId,
      playerInfo
    );
  }

  public removeSubTeamPlayer(teamId: number, subTeamId: number, playerId: number, date: string) {
    const playerRemoveBody = {
      endAt: date,
    };
    return this.__client.patch(
      this.__url + "/" + teamId + "/sub-teams/" + subTeamId + "/players/" + playerId + "/unassign",
      playerRemoveBody
    );
  }

  public getSubTeamCoaches(
    teamId: number,
    subTeamId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<PaginatedEnsueCollection<FootballTeamCoach>> {
    const teamPlayerModel = new FootballTeamCoach();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + teamId + "/sub-teams/" + subTeamId + "/coaches", options)
      .then((obj) => teamPlayerModel.createFromCollection(obj) as PaginatedEnsueCollection<FootballTeamCoach>);
  }

  public addSubTeamCoach(teamId: number, subTeamId: number, userId: number) {
    return this.__client.post(
      this.__url + "/" + teamId + "/sub-teams/" + subTeamId + "/users/" + userId + "/roles/coaches",
      null
    );
  }

  public removeSubTeamCoach(teamId: number, subTeamId: number, userId: number) {
    return this.__client.delete(
      this.__url + "/" + teamId + "/sub-teams/" + subTeamId + "/users/" + userId + "/roles/coaches"
    );
  }

  //team-tournament
  public getHostedCompetitions(
    teamId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<PaginatedEnsueCollection<ManageFootballCompetition>> {
    const model = new ManageFootballCompetition();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + teamId + "/hosted-competitions", options)
      .then((obj) => model.createFromCollection(obj) as PaginatedEnsueCollection<ManageFootballCompetition>);
  }

  public getHostedCompetitionDetail(
    teamId: number,
    subTeamId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<ManageFootballHostedCompetition> {
    const model = new ManageFootballHostedCompetition();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + teamId + "/hosted-competitions/" + subTeamId, options)
      .then((obj) => model.create(obj) as ManageFootballHostedCompetition);
  }

  public addHostedCompetition(teamId: number, hostedCompetitionInfo: IHostedCompetitionDTO) {
    return this.__client.post(this.__url + "/" + teamId + "/hosted-competitions/", hostedCompetitionInfo);
  }

  public editHostedCompetition(
    teamId: number,
    hostedCompetitionId: number,
    hostedCompetitionInfo: IHostedCompetitionDTO
  ) {
    return this.__client.patch(
      this.__url + "/" + teamId + "/hosted-competitions/" + hostedCompetitionId,
      hostedCompetitionInfo
    );
  }

  public removeHostedCompetition(teamId: number, hostedCompetitionId: number) {
    return this.__client.delete(this.__url + "/" + teamId + "/hosted-competitions/" + hostedCompetitionId);
  }

  public async getHostedTournamentManagers(
    teamId: number,
    hostedCompetitionId: number
  ): Promise<EnsueCollection<FootballTournamentManager>> {
    return this.__client.get(`${this.__url}/${teamId}/hosted-competitions/${hostedCompetitionId}/managers`);
  }

  public async addRemoveHostedTournamentManager(
    teamId: number,
    hostedCompetitionId: number,
    userId: number,
    remove = false
  ): Promise<FootballTournamentManager> {
    const url = `${this.__url}/${teamId}/hosted-competitions/${hostedCompetitionId}/users/${userId}/roles/managers`;
    if (remove) {
      return this.__client.delete(url);
    }
    return this.__client.post(url, null);
  }

  public async getTeamMatches(
    teamId: number,
    urlParams?: { [key: string]: string | number | boolean | undefined }
  ): Promise<PaginatedEnsueCollection<FootballMatchDetail>> {
    const seasonMatchListModel = new FootballMatchDetail();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + teamId + "/matches", options)
      .then((obj) => seasonMatchListModel.createFromCollection(obj) as PaginatedEnsueCollection<FootballMatchDetail>);
  }

  public async getTeamCompetitionInvites(
    teamId: number
  ): Promise<EnsueCollection<ManageFootballCompetitionInvitation>> {
    const teamInviteModel = new ManageFootballCompetitionInvitation();
    return this.__client
      .get(this.__url + "/" + teamId + "/invites")
      .then((obj) => teamInviteModel.createFromCollection(obj) as EnsueCollection<ManageFootballCompetitionInvitation>);
  }

  public async acceptTeamCompetitionInvite(teamId: number, inviteId: number, teamInviteInfo: ITeamInviteDTO) {
    return this.__client.post(this.__url + "/" + teamId + "/invites/" + inviteId + "/accept", teamInviteInfo);
  }

  public async rejectTeamCompetitionInvite(teamId: number, inviteId: number) {
    return this.__client.post(this.__url + "/" + teamId + "/invites/" + inviteId + "/reject", null);
  }
}
